import { Accordion, ActionIcon, Avatar, Button, Select, Switch, Text, Tooltip } from '@mantine/core';
import { IconMailPlus, IconPlus, IconQuestionMark, IconTrash, IconVariable } from '@tabler/icons-react';
import { ENVIRONMENT_NAMES } from '~/common/environment/names';
import { VariableNameInput } from '../common/VariableNameInput';
import VariableValueInput from '../common/VariableValueInput';
import type { SuiteForm } from './form';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { EnvironmentSelect } from '~/components/environments/EnvironmentSelect';

export function EnvironmentSection({
  form,
  orgPrefix,
  organizationEmailAddress,
  isLoadingOrganizationEmailAddress,
  variableOverrides,
}: {
  form: SuiteForm;
  orgPrefix: string;
  organizationEmailAddress?: string;
  isLoadingOrganizationEmailAddress: boolean;
  variableOverrides: { name: string; value: string; sensitive: boolean; isEditing: boolean }[];
}) {
  const { orgMetadata } = useAuthData();
  const environmentScopedEnabled = !!orgMetadata?.environmentScopedVariablesEnabled;
  return (
    <Accordion.Item value="environment">
      <Accordion.Control icon={<IconVariable size={14} />}>Environment Variables</Accordion.Control>
      <Accordion.Panel>
        <EnvironmentSelect
          className="mb-4"
          label="Environment"
          description="Environment variables will be injected into the test run"
          value={form.values.userEnvironmentId ?? null}
          onChange={(value) => form.setFieldValue('userEnvironmentId', value)}
        />

        {!!environmentScopedEnabled && (
          <Select
            className="mb-4"
            label="Variable Scope"
            placeholder="Select scope"
            data={Object.entries(ENVIRONMENT_NAMES)
              .filter(([environment]) => environment !== 'ALL' && environment !== 'NONE')
              .map(([environment, environmentName]) => ({
                value: environment,
                label: environmentName,
              }))}
            allowDeselect={false}
            {...form.getInputProps('environment')}
          />
        )}

        <div className="flex items-center gap-2">
          <Text
            size="sm"
            fw={500}
          >
            Suite Variables
          </Text>
          <Tooltip
            withArrow
            w={300}
            multiline
            color="gray"
            label="These variables will be available when you run the suite, and will override existing project-level variables with the same name."
          >
            <Avatar
              size="xs"
              radius="xl"
            >
              <IconQuestionMark size={10} />
            </Avatar>
          </Tooltip>
        </div>

        {variableOverrides.map(({ sensitive }, index) => (
          <div
            className="flex w-full gap-4 py-1"
            key={index}
          >
            <VariableNameInput
              className="flex-1"
              placeholder="Name"
              label=""
              {...form.getInputProps(`variableOverrides.${index}.name`)}
            />
            <VariableValueInput
              orgPrefix={orgPrefix}
              sensitive={sensitive}
              {...form.getInputProps(`variableOverrides.${index}.value`)}
              value={!sensitive ? variableOverrides[index].value : undefined}
            />

            <div className="flex items-start gap-2">
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                title={sensitive ? undefined : 'Populate with organization email address'}
                loading={isLoadingOrganizationEmailAddress}
                onClick={() => form.setFieldValue(`variableOverrides.${index}.value`, organizationEmailAddress ?? '')}
                disabled={sensitive}
              >
                <IconMailPlus />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                title="Remove Variable"
                onClick={() => form.removeListItem('variableOverrides', index)}
              >
                <IconTrash />
              </ActionIcon>
              <Tooltip
                withArrow
                w={220}
                multiline
                color="gray"
                label={
                  sensitive ?
                    'After creation, you and your team will not be able to switch this variable back to non-sensitive.'
                  : 'If enabled, you and your team will not be able to switch this variable back to non-sensitive after it has been created.'
                }
              >
                <div>
                  <Switch
                    label="Sensitive"
                    checked={sensitive}
                    disabled={!variableOverrides[index].isEditing && sensitive}
                    onChange={(event) => {
                      form.setFieldValue(`variableOverrides.${index}.sensitive`, event.currentTarget.checked);
                      form.setFieldValue(`variableOverrides.${index}.isEditing`, true);
                    }}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        ))}
        <div className="flex justify-start py-4">
          <Button
            variant="transparent"
            className="pl-0"
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              form.insertListItem('variableOverrides', {
                name: '',
                value: '',
                sensitive: false,
              });
            }}
          >
            Add suite variable
          </Button>
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
